import type {Instruction} from '@backstage/instructions';
import type {
  Edge,
  EdgeLookup,
  FlowMapper,
  Node,
  NodeInstanceData,
  NodeLookup,
  NodeMapper,
} from '@backstage/flows';
import {
  DEAD_END,
  getTransformations,
  NodeType,
  VALUE_NOT_PRESENT,
} from '@backstage/flows';
import {getFunc, FlowHeaders, swapInValues} from './converter-funcs';
import {getData} from './data';
import {BroadcastFunction, LogFunction} from './node.types';
import {SiteVariableLookup} from '../../../types';

const FLOW_MAPPER_CACHE: Record<string, FlowMapper> = {};

export const getFlows = (domainName?: string): FlowMapper => {
  const data = getData(domainName);
  const listenerMap: FlowMapper = {};

  if (data.key && FLOW_MAPPER_CACHE[data.key]) {
    return FLOW_MAPPER_CACHE[data.key] ?? {};
  }

  data.tabs.forEach((tab) => {
    const transformers = getTransformations(tab);

    transformers.forEach((transform) => {
      Object.values(transform.nodes).forEach((node) => {
        const id = node.element.data?.id;
        if (id && node.element.data?.type === NodeType.listener) {
          let listeners = listenerMap[id];
          if (!Array.isArray(listeners)) {
            listeners = [];
            listenerMap[id] = listeners;
          }
          listeners.push(node);
        }
      });
    });
  });

  if (data.key) {
    FLOW_MAPPER_CACHE[data.key] = listenerMap;
  }

  return listenerMap;
};

const getFieldValues = (
  refNode: Node<NodeInstanceData>
): Record<string, unknown> => {
  return (
    refNode.data?.fields?.reduce<Record<string, unknown>>((a, c) => {
      a[c.slug || ''] = c.value;
      return a;
    }, {}) || {}
  );
};

type TriggerTransformationArgs = {
  nodeMapper: NodeMapper;
  inst: Instruction;
  broadcast: BroadcastFunction;
  log: LogFunction;
  showId: string;
  structure: XMLDocument;
  siteVariableLookup: SiteVariableLookup;
};

export const triggerTransformation = async ({
  nodeMapper,
  inst,
  broadcast,
  log,
  showId,
  structure,
  siteVariableLookup,
}: TriggerTransformationArgs): Promise<void> => {
  const nodeLookup = nodeMapper.transformation?.nodes;
  const edgeLookup = nodeMapper.transformation?.edgeLookup;
  if (!edgeLookup || !nodeLookup) {
    return;
  }
  /* Higher-order function made for calling later. It allows us to treat inputs and
   * outputs essentially the same.
   */
  const applyToOutputEdges = applyToOutputEdgesCore(edgeLookup, nodeLookup);

  /* create simplified model to keep track of the state of the flow instance. */
  const nodes: LocalNode[] = Object.values(nodeLookup).map((x) => {
    return {id: x.element.id};
  });
  const edges: LocalEdge[] = Object.values(edgeLookup).map((x) => {
    return {
      id: x.element.id,
      source: x.element.source,
      sourceHandle: x.element.sourceHandle,
      target: x.element.target,
      targetHandle: x.element.targetHandle,
    };
  });

  /* Perform the call from the triggered node */
  const first = nodes.find((x) => x.id === nodeMapper.element.id);
  if (!first) return;

  first.isPressurized = true;
  first.isSpent = true;

  const refNode = nodeLookup[first.id]?.element;
  if (!refNode || !refNode.data) return;

  const initialFunc = await getFunc(refNode.data.id, 'async');

  const headers: FlowHeaders = {
    showId,
    instruction: inst,
    /* The following values will be mutated in the triggering node via `initialFunc` below */
    batter: null,
    bench: [],
    team: [],
    selector: '',
  };

  const value = initialFunc({
    node: refNode,
    data: {instruction: inst},
    broadcast,
    headers,
    log,
    structure,
    siteVariableLookup,
  });
  if (value === DEAD_END || value === undefined) {
    return;
  }
  applyToOutputEdges(first.id, value, edges, {
    isPressurized: true,
  });

  /* Handle any no-input nodes (this may be unnecessary, but I'm doing it
   * for a slight efficiency gain and to be more deterministic in the order
   * that nodes are processed) */
  nodes
    .filter((x) => !x.isSpent)
    .forEach((node) => {
      const refNode = nodeLookup[node.id]?.element;
      if (!refNode || !refNode.data) return;
      // Listeners don't work without passing along a topic and meta.
      if (refNode.data.type === NodeType.listener) return;
      if (!refNode.data.inputs?.length) {
        const thisNode = nodes.find((x) => x.id === refNode.id);
        const fieldVals = getFieldValues(refNode);
        const fn = getFunc(refNode.data.id);
        const value = fn({
          node: refNode,
          data: fieldVals,
          broadcast,
          headers,
          log: log,
          structure,
          siteVariableLookup,
        });
        if (!thisNode || value === DEAD_END || value === undefined) return;
        applyToOutputEdges(thisNode.id, value, edges, {
          isPressurized: false,
        });
        thisNode.isPressurized = false;
        thisNode.isSpent = true;
      }
    });
  /*
   * isSpent means that the node or edge has nothing of value to us anymore:
   * - A node that's completed its computation and passed its values to its output edges.
   * - An edge whose downstream-connected node has already fired.
   */
  const getSpentCount = (): number =>
    [...nodes, ...edges].filter((x) => x.isSpent).length +
    edges.filter((x) => 'value' in x).length;

  let spentCount = 0;

  /* Work through the rest of the nodes iteratively */
  while (getSpentCount() > spentCount) {
    spentCount = getSpentCount();
    const filteredNodes = nodes.filter((x) => !x.isSpent);

    // Loop with a for-await-of since forEach won't respect await. We want this
    // for loop nested in the while loop to run synchronously because each node
    // in the flow editor must run sequentially, allowing them to run in
    // parallel is undefined behavior.
    // eslint-disable-next-line no-await-in-loop
    for await (const node of filteredNodes) {
      const refNode = nodeLookup[node.id]?.element;
      if (!refNode || !refNode.data) continue;

      /* Confirm all edge inputs have values. */
      const myInputEdges = edges.filter((x) => x.target === node.id);
      const areEdgesReady =
        myInputEdges.filter((x) => 'value' in x).length == myInputEdges.length;

      if (myInputEdges.length && !areEdgesReady) continue;

      /* Create the object to pass to the func */
      const funcInput = refNode.data?.inputs?.reduce<Record<string, unknown>>(
        (a, input) => {
          const key = input.slug;
          const edge = myInputEdges.find((x) => x.targetHandle === input.slug);

          if (!edge && !input.value) {
            /* With no edge input, `input.value` defaults to an absence symbol */
            a[key] = VALUE_NOT_PRESENT;
          } else if (input.value) {
            /* The input has a value typed in. Swap in as needed. */
            a[key] = swapInValues(
              input.value,
              edge?.value,
              headers,
              siteVariableLookup
            );
          } else {
            /* `input.value` is empty, so use the edge's value */
            a[key] = edge?.value;
          }

          return a;
        },
        {}
      );

      if (!funcInput) {
        continue;
      }

      /* If it's an unpressurized listener, set isSpent=true, but don't run
       *  the function. All other cases, set isSpent=true and run function  */
      const isPressurized = myInputEdges.some((x) => x.isPressurized);

      if (!isPressurized && refNode.data.type === NodeType.listener) {
        node.isSpent = true;
      } else {
        const myFunction = await getFunc(refNode.data.id, 'async');

        /* Babel or tsc wraps `AsyncFunction` in `Function` so `myFunction.constructor.name`
         *  is useless. `await` of `Function` seems not to be a problem, though.  */
        const value = myFunction({
          node: refNode,
          data: funcInput,
          broadcast,
          headers,
          log: log,
          structure,
          siteVariableLookup,
        });

        const thisNode = nodes.find((x) => x.id === refNode.id);
        if (thisNode) {
          thisNode.isPressurized = isPressurized;
          thisNode.isSpent = true;
          if (value !== DEAD_END && value !== undefined) {
            applyToOutputEdges(thisNode.id, value, edges, {
              isPressurized,
            });
          }
        }
      }
      myInputEdges.forEach((x) => (x.isSpent = true));
    }
  }
};

const applyToOutputEdgesCore =
  (edgeLookup: EdgeLookup, nodeLookup: NodeLookup) =>
  (
    nodeId: string,
    valueObj: Record<string, unknown>,
    edges: LocalEdge[],
    obj: Partial<Omit<LocalEdge, 'id'>>
  ): void => {
    // If there are no outputs, don't bother.
    if (!nodeLookup[nodeId]?.element.data?.outputs?.length) return;
    Object.keys(valueObj).forEach((handle) => {
      const value = valueObj[handle];
      const edgeIds = Object.values(edgeLookup)
        .filter(({element: x}) => {
          return x.source === nodeId && x.sourceHandle === handle;
        })
        .map((x) => x.element.id);
      for (let i = 0; i < edges.length; i++) {
        const edge = edges[i];
        if (edge && edgeIds.includes(edge.id)) {
          edges[i] = Object.assign(edge, obj, {value});
        }
      }
    });
  };

type LocalProps = {
  id: string;
  /** Has this node resolved to a value or been determined to be impossible to fire? */
  isSpent?: boolean;
  /** Only signals downstream of listeners are pressurized. Without pressure, a listener cannot fire. */
  isPressurized?: boolean;
};

type LocalNode = LocalProps;

type LocalEdge = LocalProps & {
  value?: unknown;
} & Pick<Edge, 'source' | 'sourceHandle' | 'target' | 'targetHandle'>;
